import { useMemo } from 'react';
import useEvertecPayment from './payments/useEvertecPayment';
import useCieloTDCPayment from './payments/useCieloTDCPayment';
import useMercadoPagoTDCContext from '../../components/mercadoPago/MercadoPagoTDCContext/useMercadoPagoTDCContext';

/**
 * usePaymentEngines hook.
 * Initialize and aggregate all external payment engine SDK hooks.
 * Handles the lifecycle of Evertec, MercadoPago, and Cielo SDKs internally so
 * consumers do not need to manage each engine separately.
 * @param {object} params - Hook parameters.
 * @param {object} params.purchase - Purchase state object.
 * @param {object} params.env - Environment configuration.
 * @param {object} params.purchaser - Purchaser data.
 * @param {object} params.actions - Action callbacks (onEvertecPaymentAttempt, onConfirmEvertecPayment, onEvertecPaymentFailure).
 * @returns {object} engineHooks and enginesLoading flag.
 */
function usePaymentEngines({ purchase, env, purchaser, actions }) {
  const evertecPayment = useEvertecPayment({
    purchase,
    env,
    purchaser,
    onEvertecPaymentAttempt: actions.onEvertecPaymentAttempt,
    onConfirmEvertecPayment: actions.onConfirmEvertecPayment,
    onEvertecPaymentFailure: actions.onEvertecPaymentFailure,
  });

  const mercadoPagoTDC = useMercadoPagoTDCContext();
  const cieloTDC = useCieloTDCPayment();

  const engineHooks = useMemo(
    () => ({ evertecPayment, mercadoPagoTDC, cieloTDC }),
    [evertecPayment, mercadoPagoTDC, cieloTDC],
  );

  const enginesLoading = evertecPayment.isLoading || cieloTDC.isLoading;

  return { engineHooks, enginesLoading };
}

export default usePaymentEngines;
